(function () {
  /**
   * Currency operations and formatting.
   */

  /**
   * Prefix the wrapped, formatted amount with a minus sign when appropriate.
   *
   * The sign must be derived from the numeric input, not from the formatted
   * string. formatNumberDefault/formatForDisplay return locale-formatted strings
   * (e.g. "-85,00" for comma-decimal locales); checking `formattedAmount < 0`
   * coerces that string to NaN, so `NaN < 0` is false and the minus is left
   * embedded after the currency symbol (e.g. "€ -85,00" instead of "-€ 85,00").
   * The /[1-9]/ check avoids showing "-0.00" for values that round down to zero.
   *
   * @param Number numericValue The original, unformatted amount.
   * @param String formattedAmount The already-formatted (unsigned) amount.
   * @return string
   */
  function signAndWrap(numericValue, formattedAmount) {
    const isNegative = numericValue < 0 && /[1-9]/.test(formattedAmount);
    return (isNegative ? '-' : '') + Wahanda.Currency.wrapNumber(formattedAmount);
  }

  Wahanda.Currency = {
    /**
     * Format currency for output to user.
     *
     * @param float c Money amount
     * @param Object options Options for formatting. @see Wahanda.Number.formatNumber
     * @return string Formatted currency string
     */
    format: function (c, options) {
      const numericValue = parseFloat(c);
      const formattedAmount = Wahanda.Number.formatNumberDefault(Math.abs(numericValue), options);

      return signAndWrap(numericValue, formattedAmount);
    },

    /**
     * Wrap the given number with currenct.
     *
     * @param Number amount
     *
     * @return string
     */
    wrapNumber: function (amount) {
      const curr = App.config.get('currency');
      const currencyFormat = curr.currencyFormatCode;

      switch (currencyFormat) {
        case 'WHITESPACE_CODE':
          return `${amount} ${curr.currencyCode}`;
        case 'CODE_WHITESPACE':
          return `${curr.currencyCode} ${amount}`;
        case 'WHITESPACE_SYMBOL':
          return `${amount} ${curr.symbol}`;
        case 'SYMBOL_WHITESPACE':
          return `${curr.symbol} ${amount}`;
        case 'CODE_END':
          return amount + curr.currencyCode;
        case 'CODE_START':
          return curr.currencyCode + amount;
        case 'SYMBOL_END':
          return amount + curr.symbol;
        case 'SYMBOL_START':
          return curr.symbol + amount;
        default:
          return '';
      }
    },

    /**
     * Return the format code for current venue.
     *
     * @return string
     */
    getFormatCode: function () {
      const curr = App.config.get('currency');
      return curr.currencyFormatCode;
    },

    /**
     * If the given value is a number, return it formatted. Otherwise return an empty string.
     *
     * @param float value (optional)
     * @returns String
     */
    getFormattedOrEmpty: function (value) {
      const numericValue = parseFloat(value);
      if (Number.isNaN(numericValue)) {
        return '';
      }
      const amount = Wahanda.Number.formatForDisplay(Math.abs(numericValue));
      if (amount == null) {
        return '';
      }
      return signAndWrap(numericValue, amount);
    },

    formatFinancial: function (value) {
      return Wahanda.Currency.format(value || 0, { forceDecimalPoint: true });
    },

    /**
     * Return the given value formatter as a number (even if it's not a number).
     *
     * @param float amount (optional)
     * @returns String
     */
    getFormatted: function (value) {
      let amount = parseFloat(value);

      if (Number.isNaN(amount)) {
        amount = 0;
      }

      return Wahanda.Currency.getFormattedOrEmpty(amount);
    },

    /**
     * Returns discount %.
     *
     * @param float currentAmount
     * @param float realAmount
     * @return int
     */
    getDiscount: function (currentAmount, realAmount) {
      return Math.round(((realAmount - currentAmount) / realAmount) * 100);
    },

    /**
     * Return the symbol if it exists. Currencies with a Code will have their symbol set to null.
     * @return string
     */
    getCurrencySymbolOrCode: function () {
      const curr = App.config.get('currency');
      return curr.symbol || curr.currencyCode;
    },

    getCurrencyCode: function () {
      const curr = App.config.get('currency');
      return curr.currencyCode;
    },

    isCurrencyCode: function () {
      const curr = App.config.get('currency');
      return Boolean(curr.currencyCode);
    },
  };
})();
